// The basemap tile source, in one place.
//
// WHY THIS EXISTS. The CARTO URL was hard-coded at five call sites across four
// files (Map, Bolt-On, Consolidators, and twice in the Dossier's static tile
// mosaic). CARTO now requires an API key and stamps "API KEY REQUIRED" ACROSS
// THE TILE IMAGE ITSELF for unkeyed requests — the tiles still return 200 with a
// valid PNG, so nothing errors, nothing logs, and the only symptom is a
// watermark. That is worst in the Dossier, which is a print-ready artefact a
// client is handed.
//
// HOW TO FIX IT. Get a key from https://carto.com/basemaps/apikey — free tier,
// 5 million tile requests a month, issued by email immediately with no account
// and no approval queue. Then set VITE_BASEMAP_TILE_URL in the web app's env to
// the exact template CARTO sends back.
//
// The env var is the FULL TEMPLATE rather than just a key, deliberately:
//   * CARTO's keyed URL shape (path prefix and query-parameter name) should be
//     taken from the email that carries the key, not guessed here.
//   * It makes switching provider — MapTiler, Stadia, a self-hosted Protomaps —
//     a config change instead of a code change across five call sites.
//
// ⚠️ Do NOT substitute OpenStreetMap's own tile.openstreetmap.org. Their tile
// usage policy is written for light and non-commercial use and asks commercial
// products to run their own or use a provider; it is not a free replacement for
// a keyed basemap.
//
// A basemap key is public by nature — it ships in the browser bundle — so it
// should be restricted to the app's domains at the provider, not treated as a
// secret.

/** Leaflet raster template. `{s}` subdomain, `{r}` retina suffix. */
export const BASEMAP_TILE_URL =
  (import.meta.env.VITE_BASEMAP_TILE_URL as string | undefined) ??
  'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

export const BASEMAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

/** True when no key is configured, so the tiles will carry CARTO's watermark. */
export const BASEMAP_UNKEYED = !import.meta.env.VITE_BASEMAP_TILE_URL;

/**
 * One tile URL for the Dossier's static mosaic, which builds `<img>` tags rather
 * than using Leaflet. Resolves the same template so the print artefact and the
 * interactive panels can never drift apart.
 */
export function basemapTileSrc(z: number, x: number, y: number, sub?: string): string {
  // `sub` comes from an indexed lookup at the call site, so it is optional here
  // rather than forcing a non-null assertion there. Providers that ignore {s}
  // simply never see it.
  return BASEMAP_TILE_URL.replace('{s}', sub ?? 'a')
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y))
    .replace('{r}', '@2x');
}
